import React, { useState, useEffect, useMemo } from "react";

let timeout;

/**
 * Este hook permite paginar un arreglo, 
 * recibe como parametro un objeto en donde se puede definir el arreglo que se quiere paginar, 
 * la cantidad de items que van por página,
 * y el delay en milisegundos para que se cargue una nueva página.
 * Es util para el scroll infinito de arreglos sincronos, para el paginado asincrono en donde se debe hacer fetch se debe usar useAsyncPagination
 * Este hook permite paginar un arreglo.
 * @param {Object} options - Objeto que define las opciones de paginación.
 * @param {number} options.itemsPerPage - Cantidad de elementos por página.
 * @param {Array<any>} options.data - Arreglo que se quiere paginar.
 * @param {number} options.nextPageDelay - Retraso en milisegundos para cargar la siguiente página.
 * @returns {{hasMore:boolean, nextPage:Promise<boolean>, paginatedData:Array<any>}}  - Objeto con propiedades relacionadas a la paginación.
 * @property {boolean} hasMore - Indica si hay más elementos por cargar.
 * @property {Promise} nextPage - Promesa que se resuelve cuando se carga la siguiente página.
 * @property {Array<any>} paginatedData - Arreglo con los datos paginados.
retorna un objeto con propiedades relacioandas al paginado.
 */
export const useSyncPagination = ({ itemsPerPage, data, nextPageDelay }) => {
	const [currentPage, setCurrentPage] = useState(1);
	const [paginatedData, setPaginatedData] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const memoized = React.useMemo(() => data, [JSON.stringify(data)]);

	itemsPerPage = itemsPerPage >= 1 ? itemsPerPage : 1;

	useEffect(() => {
		setPaginatedData(data.slice(0, currentPage * itemsPerPage));
	}, [currentPage, memoized, itemsPerPage]);

	useEffect(() => {
		setHasMore(paginatedData.length < data.length);
	}, [data, paginatedData]);

	const nextPage = async () => {
		clearTimeout(timeout);
		return new Promise((resolve, reject) => {
			if (nextPageDelay > 0) {
				timeout = setTimeout(() => {
					setCurrentPage(currentPage + 1);
					resolve(true);
				}, nextPageDelay);
			} else {
				setCurrentPage(currentPage + 1);
				resolve(true);
			}
		});
	};

	return { paginatedData, nextPage, hasMore };
};



/**
 * Hook que permite paginar un array de datos.
 * @param {{ itemsPerPage: number, data: any[], onChangePage: (page: number) => void, defaultPage: number }} props
 * @returns {{
 *  currentPageData: any[],
 *  currentPage: number,
 *  nextPage: () => void,
 *  prevPage: () => void,
 *  firstPage: () => void,
 *  lastPage: () => void,
 *  goToPage: (page: number) => void,
 *  range: number[];
 *  totalPages: number
 * }} pagination object
 */
export const usePagination = ({ itemsPerPage = 20, data, onChangePage, defaultPage }) => {
	itemsPerPage = itemsPerPage >= 1 ? itemsPerPage : 1;
	const memoizedData = React.useMemo(() => (Array.isArray(data) ? data : []), [data]);
    const totalPages = useMemo(
        () => Math.ceil(memoizedData.length / itemsPerPage),
        [memoizedData, itemsPerPage]
    );
    defaultPage = defaultPage >= 1 && defaultPage <= totalPages ? defaultPage : 1;
	const [currentPage, setCurrentPage] = useState(defaultPage || 1);
	const currentPageData = useMemo(() => {
		return memoizedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
	}, [memoizedData, currentPage, itemsPerPage]);

	const range = useMemo(
		() => getPaginationRange({ currentPage, totalPages }),
		[currentPage, totalPages]
	);

	const nextPage = () => {
		if (currentPage === totalPages) return;
		setCurrentPage((page) => page + 1);
	};

	const prevPage = () => {
		if (currentPage === 1) return;
		setCurrentPage((page) => page - 1);
	};

	const firstPage = () => {
		setCurrentPage(1);
	};

	const lastPage = () => {
		setCurrentPage(totalPages);
	};

	const goToPage = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const handleOnChange = () => {
		onChangePage && onChangePage(currentPage);
	};

	useEffect(() => {
		handleOnChange();
	}, [currentPage]);

    useEffect(() => {
	setCurrentPage(defaultPage);
}, [defaultPage]);

	return {
		currentPageData,
		currentPage,
		nextPage,
		prevPage,
		firstPage,
		lastPage,
		goToPage,
		range,
		totalPages,
	};
};

/**
 * Genera un arreglo con las páginas a mostrar en una paginación.
 * @param {{ currentPage: number, totalPages: number, siblingCount?: number }} options - Opciones de paginación.
 * @param {number} options.currentPage - Número de la página actual.
 * @param {number} options.totalPages - Número total de páginas.
 * @param {number} [options.siblingCount=1] - Cantidad de páginas vecinas a mostrar.
 * @returns {Array<number|string>} - Arreglo con las páginas a mostrar. Las páginas se representan como números y los huecos se representan como strings '...'.
 */
function getPaginationRange({ currentPage, totalPages, siblingCount = 1 }) {
  const totalPageNumbers = siblingCount * 2 + 5;

  // Si hay pocas páginas, las mostramos todas
  if (totalPages <= totalPageNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const firstPage = 1;
  const lastPage = totalPages;

  const leftSiblingIndex = Math.max(currentPage - siblingCount, firstPage + 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, lastPage - 1);

  const range = [];

  // Página 1 siempre visible
  range.push(firstPage);

  // Agregar elipsis si hay hueco entre la primera y los vecinos
  if (leftSiblingIndex > firstPage + 1) {
    range.push('...');
  }

  // Agregar las páginas vecinas del currentPage
  for (let i = leftSiblingIndex; i <= rightSiblingIndex; i++) {
    range.push(i);
  }

  // Agregar elipsis si hay hueco entre los vecinos y la última
  if (rightSiblingIndex < lastPage - 1) {
    range.push('...');
  }

  // Página final siempre visible
  range.push(lastPage);

  return range;
}
